<template>
  <div>
    <v-row class="my-4" v-if="!loading && totalRecords > 0">
      <v-col cols="12" class="d-flex justify-end align-end">
        <ColumnsSelector
          persistenceKey="teamActivity__routinesTab"
          :headers="headers"
          :limitActiveColumns="columnsLimit"
          @toggleColumn="onToggleColumn"
          @close="trackColumnsSelector"
          @setInitialColumns="setInitialColumns"
        />
      </v-col>
    </v-row>

    <v-data-table
      v-show="!loading && totalRecords > 0"
      data-private
      calculate-widths
      class="custom-table"
      :footer-props="footerOptions"
      :headers="columns"
      :items="rows"
      :mobile-breakpoint="$vuetify.breakpoint.thresholds.md"
      :options.sync="tableOptions"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :server-items-length="totalRecords"
      :loading="loading"
      :no-data-text="$t('routines.definitions_empty_state')"
      @click:row="goToRoutine($event)"
    >
      <template v-slot:[`item.name`]="{ item }">
        <v-tooltip top max-width="300px">
          <template v-slot:activator="{ on }">
            <router-link
              class="router-link"
              :to="`/routine-executions/${item.id}`"
            >
              <span v-on="on" v-snip="1" class="body-2 link-td">{{
                item.name
              }}</span>
            </router-link>
          </template>
          {{ item.name }}
        </v-tooltip>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <Status :entity="item" />
      </template>

      <template v-slot:[`item.createdAt`]="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">{{
          item.createdAt
        }}</span>
      </template>

      <template v-slot:[`item.routineAssignee`]="{ item }">
        <ChipAvatar small :account="item.routineAssignee" />
      </template>

      <template v-slot:[`item.findingsProgress`]="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">{{
          item.findingsProgress
        }}</span>
      </template>

      <template v-slot:[`item.findingsCompleted`]="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">{{
          item.findingsCompleted
        }}</span>
      </template>

      <template v-slot:[`item.categories`]="{ item }">
        <ChipList outlined :items="item.categories" />
      </template>

      <template v-slot:[`item.tags`]="{ item }">
        <ChipList outlined v-if="item.tags.length > 0" :items="item.tags" />
        <div v-else class="text-start">-</div>
      </template>

      <template v-slot:[`item.createdBy`]="{ item }">
        <ChipAvatar small :account="item.createdBy" />
      </template>
    </v-data-table>

    <TeamActivityEmptyState v-if="!loading && totalRecords === 0" />

    <TableSkeleton v-if="loading" :columns="columns.length" class="pt-12" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

import ChipAvatar from '@/components/ChipAvatar.vue'
import Status from '@/components/Core/StatusChip.vue'
import ChipList from '../ChipList.vue'
import TeamActivityEmptyState from './TeamActivityEmptyState.vue'
import ColumnsSelector from '@/components/ColumnsSelector.vue'
import TableSkeleton from '@/components/TableSkeleton.vue'

const INITIAL_ITEMS_PER_PAGE = 20

export default {
  name: 'RoutinesTab',

  props: {
    teamId: {
      type: String,
      required: false,
    },

    columnsLimit: {
      type: Number,
      required: false,
      default: null,
    },
  },

  mixins: [mixin, trackingMixin],

  components: {
    ChipAvatar,
    Status,
    ChipList,
    TeamActivityEmptyState,
    TableSkeleton,
    ColumnsSelector,
  },

  data() {
    return {
      routines: [],
      loading: false,
      totalRecords: 0,
      sortBy: 'createdAt',
      sortDesc: true,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('routines.routine_execution'),
          value: 'name',
          show: true,
          width: 234,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
          show: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('home.teams_activity.execution_date'),
          value: 'createdAt',
          show: true,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 150,
        },
        {
          text: this.$t('filters.date.modified_date'),
          value: 'updatedAt',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
          width: 150,
        },
        {
          text: this.$t('home.teams_activity.routine_assignee'),
          value: 'routineAssignee',
          show: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('home.teams_activity.findings_progress'),
          value: 'findingsProgress',
          show: false,
          align: 'center',
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('home.teams_activity.findings_completed'),
          value: 'findingsCompleted',
          show: false,
          align: 'center',
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.category_other'),
          value: 'categories',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('common.tag_other'),
          value: 'tags',
          show: false,
          cellClass: 'cursor-pointer blue-grey--text text--darken-3',
        },
        {
          text: this.$t('home.teams_activity.created_by'),
          value: 'createdBy',
          show: false,
          cellClass: 'cursor-pointer',
        },
      ],
    }
  },

  methods: {
    async fetchRoutines(options) {
      try {
        this.loading = true
        const { data } = await api.routine.getRoutineExecutions(
          this.organizationId,
          {
            filters: {
              teams: [this.teamId],
            },
            sort: {
              by: options.sortBy ?? 'createdAt',
              order: options.sortDesc ? 'desc' : 'asc',
            },
            relations: ['tags', 'createdBy.photo'],
            limit: options.itemsPerPage,
            skip: options.itemsPerPage * (options.page - 1),
          }
        )
        this.routines = data.results ?? []
        this.totalRecords = data.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    getParsedCategories(categories) {
      return (
        categories?.map(category => this.$t(`common.categories.${category}`)) ??
        []
      )
    },
    getParsedTags(tags) {
      return tags?.map?.(tag => tag.name) ?? []
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
    },

    goToRoutine(routine) {
      this.trackHomeNavigate({
        origin: 'teamActivity',
        destination: 'routineDetail',
      })
      this.navigateTo(`/routine-executions/${routine.id}`)
    },

    trackColumnsSelector() {
      this.trackTeamNavigate({
        section: 'routinesTab',
        element: 'columnsSelector',
        selectedColumns: this.headers
          .filter(col => col.show === true)
          .map(col => col.text),
      })
    },

    setInitialColumns(selectedColumns) {
      this.headers = this.getInitialSelectedTableColumns(
        this.headers,
        selectedColumns
      )
    },
  },

  computed: {
    ...mapGetters(['organizationId']),

    rows() {
      return this.routines.map(routine => {
        return {
          id: routine.id,
          name: routine.name,
          status: routine.status,
          createdBy: routine.createdBy,
          createdAt: routine.createdAt
            ? utils.dates.formatDate(routine.createdAt, 'MMM D, YYYY, h:mm A')
            : '',
          updatedAt: routine.updatedAt
            ? utils.dates.formatDate(routine.updatedAt, 'MMM D, YYYY, h:mm A')
            : '',
          routineAssignee: routine.createdBy,
          findingsProgress: routine.findingsInProgress ?? 0,
          findingsCompleted: routine.findingsCompleted ?? 0,
          categories: this.getParsedCategories(routine.categories),
          tags: this.getParsedTags(routine.tags),
        }
      })
    },

    columns() {
      return this.headers.filter(h => h.show)
    },

    tableOptions: {
      get() {
        return {
          itemsLength: this.totalRecords,
          itemsPerPage: INITIAL_ITEMS_PER_PAGE,
          page: 1,
          multiSort: false,
        }
      },

      set(event) {
        this.fetchRoutines({
          itemsLength: this.totalRecords,
          itemsPerPage: event.itemsPerPage,
          page: event.page,
          sortDesc: event.sortDesc[0] ?? true,
          sortBy: event.sortBy[0],
        })
      },
    },
  },

  watch: {
    teamId() {
      this.fetchRoutines(this.tableOptions)
    },
  },
}
</script>
<style lang="scss" scoped>
.v-application a.router-link {
  color: $color-blue-grey-darken3;
  text-decoration: none;
}
</style>
